import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import AnnouncementContent from './AnnouncementContent';
import Modal from '../Modal';
import Button from '../Button';
import IconClose from '../../svg/icons/ic_close_alt_42x42.svg';
import containerQueryProps from '../../props/containerQueryProps';
import announcementTypes, { EVENT_TYPE, VIEW_STATE } from './announcementTypes';
import announcementEvents from './announcementEvents';
import { getTranslation } from '../../providers/TranslationProvider';
import { hexify } from '../../utils';

import './Announcement.scss';
import { getUserProfileLocations, postUserProfileLocations } from '../../api';
import SuccessView from '../SuccessView/SuccessView';
import useTrackEvent from '../../hooks/useTrackEvent';

const { HEADER, POPUP, FOOTER } = announcementTypes;
const {
  CTA_TYPE,
} = announcementEvents;

const getFooterStyles = (baseOverlay, baseContent, background, containerQuery) => {
  let overlay = {
    ...baseOverlay,
    backgroundColor: 'none',
    bottom: '0',
    top: 'initial',
  };
  let content = {
    ...baseContent,
    maxWidth: '100%',
    borderRadius: 0,
    overflow: 'hidden',
    padding: '40px 30px 30px 30px',
    width: '100%',
  };

  if (containerQuery.sm) {
    overlay = {
      ...overlay,
      boxShadow: '0 -2px 5px 0 rgba(0, 0, 0, 0.08)',
      backgroundColor: hexify(background) || '#fff',
      height: 'auto',
      padding: '0',
    };

    content = {
      ...content,
      position: 'initial',
      height: '100%',
      boxShadow: 'none',
      border: 'none',
    };
  }

  if (containerQuery.lg) {
    content = {
      ...content,
      padding: '15px 30px',
    };
  }

  return {
    overlay,
    content,
  };
};

const AnnouncementModal = ({
  $rootScope,
  bodyOpenClassName,
  type,
  isOpen,
  id,
  heading,
  title,
  content,
  image,
  colors,
  containerQuery,
  operation,
  primaryLinkText,
  primaryLinkUrl,
  secondaryLinkText,
  secondaryLinkUrl,
  showRemindMeLater,
  remindMeLaterCount,
  markAnnouncementViewed,
  event,
  isProcessing: isProcessingProp,
  viewState: viewStateProp,
}) => {
  const [isOpenState, setIsOpen] = useState(isOpen);
  const [isProcessing, setIsProcessing] = useState(isProcessingProp);
  const [viewState, setViewState] = useState(viewStateProp);
  const [showSuccessView, setShowSuccessView] = useState(false);

  useTrackEvent(() => {
    event(EVENT_TYPE.VIEW_ANNOUNCEMENT, {
      type,
      title,
      id,
      // remindMeLaterCount is the view count till now. We should increment it by 1 to account for current placement.
      ...(showRemindMeLater && { label: `${remindMeLaterCount + 1} view` }),
    });
  });

  const getModalStyles = () => {
    const overlay = {
      backgroundColor: 'rgba(0, 0, 0, 0.25)',
      zIndex: '10',
      display: 'flex',
      alignItems: containerQuery.sm ? 'center' : 'flex-end',
    };

    const modalContent = {
      position: 'relative',
      left: '0',
      top: '0',
      right: '0',
      bottom: '0',
      maxWidth: '600px',
      margin: '0 auto',
      border: 'none',
      borderRadius: '10px',
      boxShadow: '0 10px 50px 0 rgba(0, 0, 0, 0.1)',
      padding: '30px',
      backgroundColor: hexify(colors?.background) || '#fff',
      overflowX: 'hidden',
    };

    if (type === FOOTER) {
      return getFooterStyles(overlay, modalContent, colors?.background, containerQuery);
    }

    return {
      overlay,
      content: modalContent,
    };
  };

  const showAuthModal = () => {
    $rootScope.$broadcast('MODAL', 'signin');
  };

  const closeModal = (shouldMarkAnnouncementViewed = true) => {
    setIsOpen(false);
    setShowSuccessView(false);
    // In case user clicks remind me later, we should not mark announcement viewed
    // as that would hide announcement forever for that UUID.
    if (shouldMarkAnnouncementViewed) {
      // Mark announcement as viewed, when user closes the pop-up
      markAnnouncementViewed?.();
    }
  };

  const handleSecondaryButtonClick = () => {
    if (showRemindMeLater) {
      markAnnouncementViewed?.(showRemindMeLater);
      setShowSuccessView(true);
    } else if (secondaryLinkUrl) {
      window.open(secondaryLinkUrl, '_blank');
      markAnnouncementViewed?.();
    } else {
      // If showRemindMeLater is true, we close the modal without marking the announcement as viewed
      // as we should mark as remind_me_later
      closeModal(!showRemindMeLater);
    }
    event(EVENT_TYPE.CLICK_ANNOUNCEMENT, {
      type,
      title,
      id,
      cta: CTA_TYPE.secondary,
      // remindMeLaterCount is the view count till now. We should increment it by 1 to account for current placement.
      ...(showRemindMeLater && { label: `${remindMeLaterCount + 1} view` }),
    });
  };

  const optInToMarketing = () => {
    setIsProcessing(true);
    setViewState(VIEW_STATE.LOADING);

    getUserProfileLocations()
      .then(
        (locations) => {
          postUserProfileLocations({
            locationCodes: locations?.locations,
            isMarketable: true,
            primaryLocationCode: locations?.primary,
          })
            .then(() => {
              setIsProcessing(false);
              setViewState(VIEW_STATE.SUCCESS);
            })
            .catch(() => {
              setIsProcessing(false);
              setViewState(VIEW_STATE.ERROR);
            });
        }
      )
      .catch((err) => {
        setIsProcessing(false);
        setViewState(VIEW_STATE.ERROR);

        if (err?.status === 419) {
          showAuthModal();
        }
      });
  };

  const handlePrimaryButtonClick = () => {
    if (primaryLinkUrl) {
      window.open(primaryLinkUrl, '_blank');
    }

    if (operation) {
      optInToMarketing();
    } else {
      closeModal();
    }

    event(EVENT_TYPE.CLICK_ANNOUNCEMENT, {
      type,
      title,
      id,
      cta: CTA_TYPE.primary,
    });
  };

  return (
    <Modal
      bodyOpenClassName={bodyOpenClassName}
      className={cx({ 'ReactModal__Content--footer': type === FOOTER })}
      shouldCloseOnOverlayClick={type === POPUP}
      shouldCloseOnEsc={type === POPUP}
      isOpen={isOpenState}
      style={getModalStyles()}
      onRequestClose={closeModal}
    >
      <Button
        variant="no-style"
        className="AnnouncementModal__icon-close"
        onClick={closeModal}
        data-test-id="announcement_modal-button-close"
        aria-label={getTranslation({ translationKey: 'close', fallback: 'Close' })}
      >
        <IconClose
          background={false}
        />
      </Button>

      {showSuccessView ? (
        <SuccessView
          successEventDescription={getTranslation({
            translationKey: 'remind_me_later_success_text',
            fallback: 'We\'ll remind you about this next time',
          })}
          successIconAltText={getTranslation({
            translationKey: 'success_icon',
            fallback: 'Success icon',
          })}
          closeModal={() => closeModal(false)}
          autoCloseDuration={5000}
        />
      ) : (
        <AnnouncementContent
          heading={heading}
          title={title}
          content={content}
          image={image}
          processing={isProcessing}
          operation={operation}
          primaryLinkText={primaryLinkText}
          secondaryLinkText={secondaryLinkText}
          colors={colors}
          mobile={containerQuery.sm}
          onPrimaryLinkClick={handlePrimaryButtonClick}
          onSecondaryLinkClick={handleSecondaryButtonClick}
          type={type}
          viewState={viewState}
        />
      )}
    </Modal>
  );
};

AnnouncementModal.propTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func,
  }).isRequired,
  bodyOpenClassName: PropTypes.string,
  type: PropTypes.oneOf([HEADER, POPUP, FOOTER]),
  isOpen: PropTypes.bool,
  id: PropTypes.string.isRequired,
  heading: PropTypes.string,
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  image: PropTypes.string,
  colors: PropTypes.shape({
    heading: PropTypes.string,
    title: PropTypes.string,
    font: PropTypes.string,
    background: PropTypes.string,
    primaryButton: PropTypes.shape({
      font: PropTypes.string,
      background: PropTypes.string,
    }),
    secondaryButton: PropTypes.shape({
      font: PropTypes.string,
      background: PropTypes.string,
    }),
  }),
  containerQuery: containerQueryProps,
  operation: PropTypes.number,
  primaryLinkText: PropTypes.string,
  primaryLinkUrl: PropTypes.string,
  secondaryLinkText: PropTypes.string,
  secondaryLinkUrl: PropTypes.string,
  showRemindMeLater: PropTypes.bool,
  remindMeLaterCount: PropTypes.number,
  markAnnouncementViewed: PropTypes.func,
  event: PropTypes.func,
  isProcessing: PropTypes.bool.isRequired,
  viewState: PropTypes.shape(Object.keys(VIEW_STATE)).isRequired,
};
AnnouncementModal.defaultProps = {
  bodyOpenClassName: undefined,
  type: POPUP,
  isOpen: true,
  heading: '',
  image: '',
  colors: {
    primaryButton: {},
    secondaryButton: {},
  },
  containerQuery: {
    xs: true,
    sm: true,
  },
  operation: undefined,
  primaryLinkText: '',
  primaryLinkUrl: '',
  secondaryLinkText: '',
  secondaryLinkUrl: '',
  showRemindMeLater: false,
  remindMeLaterCount: 0,
  markAnnouncementViewed: () => { },
  event: () => { },
};

AnnouncementModal.displayName = 'AnnouncementModal';
export default AnnouncementModal;
